import {
  getGetApiGroupQueryOptions,
  getGetApiInstitutionQueryOptions,
  getGetApiLabQueryOptions,
  getGetApiUserListQueryOptions,
  type UserDTO,
} from '@smartsash/api-client';
import type { TableFilterFieldOverrides } from '@smartsash/ui';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  USERS_ACTIVE_UNTIL_FIELD,
  USERS_CERTIFICATION_FIELD,
  USERS_ENABLED_FIELD,
  USERS_GROUP_FIELD,
  USERS_INSTITUTION_FIELD,
  USERS_LAB_FIELD,
  USERS_ROLE_FIELD,
  USERS_ROLES,
  usersColumnText,
  usersQueryParams,
  type UsersSearch,
} from './usersModel';

import { LOOKUP_PAGE_SIZE } from '@/hooks/useLookupNames';
import { DataScreenFilter, filterColumnsFromText } from '@/ui/shared/TableFilter';

/**
 * The filter builder for the Users list (HS-869 + the filtering-foundation follow-up).
 *
 * The rows come from the screen's own cached query — `usersQueryParams` builds it identically here
 * and in `usersScreen`, since both now read `GET /api/User/list`. Institution carries ids behind
 * institution names and certification carries `true`/`false` behind the badge labels, matching the
 * table; role, group, laboratory and account status have no column at all and are appended as extra
 * fields, and the deactivation date column becomes a range rather than free text or an enumeration.
 */
export function UsersFilter({ search }: { search: UsersSearch }) {
  const { t } = useTranslation();
  const { data } = useQuery(getGetApiUserListQueryOptions(usersQueryParams(search)));
  const { data: institutions } = useQuery(
    getGetApiInstitutionQueryOptions({ page: 1, pageSize: LOOKUP_PAGE_SIZE }),
  );
  const { data: groups } = useQuery(
    getGetApiGroupQueryOptions({ page: 1, pageSize: LOOKUP_PAGE_SIZE }),
  );
  const { data: labs } = useQuery(
    getGetApiLabQueryOptions({ page: 1, pageSize: LOOKUP_PAGE_SIZE }),
  );

  const columns = useMemo(
    () =>
      filterColumnsFromText<UserDTO>(
        {
          name: usersColumnText.name,
          institution: usersColumnText.institution,
          email: usersColumnText.email,
          certification: (row) => usersColumnText.certification(row, (key) => t(`users.${key}`)),
          deactivationDate: usersColumnText.deactivationDate,
        },
        (id) =>
          id === 'institution' ? t('users.details.institute') : t(`users.columns.${id as 'email'}`),
      ),
    [t],
  );

  const overrides = useMemo<TableFilterFieldOverrides>(
    () => ({
      labels: {
        [USERS_INSTITUTION_FIELD]: t('users.filters.institute'),
        [USERS_CERTIFICATION_FIELD]: t('users.filters.certification'),
      },
      // The deactivation date is a column, but it reads as free text or as an enumeration depending
      // on how many rows repeat a date — neither of which is the range the endpoint accepts.
      types: { [USERS_ACTIVE_UNTIL_FIELD]: 'date' },
      options: {
        [USERS_INSTITUTION_FIELD]: (institutions?.items ?? [])
          .filter((institution) => institution.id != null && institution.name != null)
          .map((institution) => ({ value: institution.id ?? '', label: institution.name ?? '' })),
        [USERS_CERTIFICATION_FIELD]: [
          { value: 'true', label: t('users.filters.certified') },
          { value: 'false', label: t('users.filters.uncertified') },
        ],
      },
      extra: [
        {
          id: USERS_ROLE_FIELD,
          label: t('users.filters.role'),
          type: 'enum',
          options: USERS_ROLES.map((role) => ({ value: role, label: role })),
        },
        {
          id: USERS_GROUP_FIELD,
          label: t('users.filters.group'),
          type: 'enum',
          options: (groups?.items ?? [])
            .filter((group) => group.id && group.name)
            .map((group) => ({ value: group.id ?? '', label: group.name ?? '' })),
        },
        {
          id: USERS_LAB_FIELD,
          label: t('users.filters.laboratory'),
          type: 'enum',
          options: (labs?.items ?? [])
            .filter((lab) => lab.id && lab.name)
            .map((lab) => ({ value: lab.id ?? '', label: lab.name ?? '' })),
        },
        {
          id: USERS_ENABLED_FIELD,
          label: t('users.filters.status'),
          type: 'enum',
          options: [
            { value: 'true', label: t('users.filters.enabled') },
            { value: 'false', label: t('users.filters.disabled') },
          ],
        },
      ],
    }),
    [groups, institutions, labs, t],
  );

  return <DataScreenFilter columns={columns} rows={data?.items ?? []} overrides={overrides} />;
}
